import { useEffect, useState } from "react";
import { http, API_BASE } from "@/lib/api";
import { Gift, Plus, Search, Download as DLIcon, Eye, Ban, RotateCcw, Send, DollarSign, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { v: "all", l: "All" },
  { v: "active", l: "Active" },
  { v: "pending_payment", l: "Pending" },
  { v: "redeemed", l: "Redeemed" },
  { v: "expired", l: "Expired" },
  { v: "refunded", l: "Refunded" },
  { v: "disabled", l: "Disabled" },
];

const statusPill = (s) => {
  const map = {
    active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    pending_payment: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
    redeemed: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
    expired: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
    refunded: "bg-red-500/15 text-red-300 border-red-500/30",
    disabled: "bg-red-500/15 text-red-400 border-red-500/30",
  };
  return map[s] || "bg-white/5 text-white/60 border-white/10";
};

export default function GiftCardsAdmin() {
  const [stats, setStats] = useState(null);
  const [cards, setCards] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [showIssue, setShowIssue] = useState(false);
  const [detail, setDetail] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        http.get("/admin/gift-cards/stats"),
        http.get("/admin/gift-cards", { params: { status: statusFilter, q: q || undefined } }),
      ]);
      setStats(s.data);
      setCards(c.data);
    } catch (e) {
      toast.error("Could not load gift cards");
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line
  useEffect(() => { refresh(); }, [statusFilter]);
  useEffect(() => {
    const t = setTimeout(refresh, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const action = async (card, kind, extra = {}) => {
    try {
      if (kind === "disable") await http.post(`/admin/gift-cards/${card.id}/disable`);
      else if (kind === "refund") await http.post(`/admin/gift-cards/${card.id}/refund`);
      else if (kind === "resend") await http.post(`/admin/gift-cards/${card.id}/resend`);
      else if (kind === "balance") {
        await http.patch(`/admin/gift-cards/${card.id}/balance`, {
          new_balance: extra.new_balance,
          reason: extra.reason || "Manual adjustment",
        });
      }
      toast.success(`${kind.replace("_", " ")} done.`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const downloadCsv = async () => {
    const tok = localStorage.getItem("stc_token");
    try {
      const r = await fetch(`${API_BASE}/api/admin/gift-cards/export.csv`, { headers: { Authorization: `Bearer ${tok}` } });
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `stc-gift-cards-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Could not export CSV"); }
  };

  return (
    <div className="space-y-6" data-testid="gift-cards-admin">
      {/* Stats */}
      {stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile label="Outstanding Liability" value={`$${stats.outstanding_liability.toFixed(2)}`} accent testid="gc-stat-liability"/>
          <StatTile label="Lifetime Sold" value={`$${stats.lifetime_sold.toFixed(2)}`} testid="gc-stat-sold"/>
          <StatTile label="Active Cards" value={stats.active_cards} testid="gc-stat-active"/>
          <StatTile label="Total Cards" value={stats.total_cards} testid="gc-stat-total"/>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {STATUSES.map(s => (
            <button key={s.v}
              data-testid={`gc-filter-${s.v}`}
              onClick={() => setStatusFilter(s.v)}
              className={`font-ui text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 border ${statusFilter === s.v ? "bg-[#C9A055] text-black border-[#C9A055]" : "border-white/15 text-[#F4F1EA] hover:border-[#C9A055]"}`}>
              {s.l}{statusFilter === s.v && stats && s.v !== "all" ? ` (${stats.by_status[s.v] ?? 0})` : ""}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 text-[#737373]"/>
            <input data-testid="gc-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search code / email / name" className="stc-input pl-9 w-[280px]"/>
          </div>
          <button data-testid="gc-export-csv" onClick={downloadCsv} className="btn-ghost"><DLIcon size={14}/> CSV</button>
          <button data-testid="gc-issue-btn" onClick={() => setShowIssue(true)} className="btn-gold"><Plus size={14}/> Issue Comp Card</button>
        </div>
      </div>

      {/* Table */}
      <div className="border border-white/10 bg-[#0a0a0a] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-[#9a9690] font-ui text-[10px] tracking-[0.22em] uppercase">
            <tr>
              <th className="text-left px-4 py-3">Code</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-right px-4 py-3">Balance</th>
              <th className="text-left px-4 py-3">Purchaser</th>
              <th className="text-left px-4 py-3">Recipient</th>
              <th className="text-left px-4 py-3">Payment</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="text-center py-8 text-[#737373]"><Loader2 size={18} className="inline animate-spin"/> Loading…</td></tr>
            )}
            {!loading && cards.length === 0 && (
              <tr><td colSpan={9} className="text-center py-12 text-[#737373]" data-testid="gc-empty">No gift cards match these filters yet.</td></tr>
            )}
            {!loading && cards.map(c => (
              <tr key={c.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`gc-row-${c.code}`}>
                <td className="px-4 py-3 font-mono text-[#F5F2EB]">{c.code}</td>
                <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 border font-ui text-[9px] tracking-[0.22em] uppercase ${statusPill(c.status)}`}>{c.status.replace("_"," ")}</span></td>
                <td className="px-4 py-3 text-right text-[#F5F2EB]">${(c.amount).toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-[#C9A055] font-semibold">${(c.balance).toFixed(2)}</td>
                <td className="px-4 py-3 text-[#E8E0CC]"><div>{c.purchaser_name}</div><div className="text-[10px] text-[#737373]">{c.purchaser_email}</div></td>
                <td className="px-4 py-3 text-[#E8E0CC]"><div>{c.recipient_name}</div><div className="text-[10px] text-[#737373]">{c.recipient_email}</div></td>
                <td className="px-4 py-3 text-[#9a9690]">{c.payment_method}{c.payment_status && c.payment_status !== "paid" ? ` (${c.payment_status})` : ""}</td>
                <td className="px-4 py-3 text-[#9a9690]">{(c.created_at || "").slice(0,10)}</td>
                <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                  <button onClick={() => setDetail(c)} className="p-1.5 border border-white/15 text-[#9a9690] hover:text-[#C9A055] hover:border-[#C9A055]" title="View" data-testid={`gc-view-${c.code}`}><Eye size={13}/></button>
                  {c.status === "active" && (
                    <>
                      <button onClick={() => {
                        const v = prompt(`Adjust balance for ${c.code}. Current: $${c.balance}. New balance:`, c.balance);
                        if (v === null) return;
                        const nv = parseFloat(v);
                        if (isNaN(nv) || nv < 0) { toast.error("Invalid amount"); return; }
                        const r = prompt("Reason (audit log):", "Manual adjustment");
                        if (r === null) return;
                        action(c, "balance", { new_balance: nv, reason: r });
                      }} className="p-1.5 border border-white/15 text-[#9a9690] hover:text-[#C9A055] hover:border-[#C9A055]" title="Adjust balance" data-testid={`gc-balance-${c.code}`}><DollarSign size={13}/></button>
                      <button onClick={() => action(c, "resend")} className="p-1.5 border border-white/15 text-[#9a9690] hover:text-[#C9A055] hover:border-[#C9A055]" title="Resend delivery" data-testid={`gc-resend-${c.code}`}><Send size={13}/></button>
                      <button onClick={() => { if (window.confirm(`Disable ${c.code}? Recipient will no longer be able to redeem.`)) action(c, "disable"); }} className="p-1.5 border border-white/15 text-[#9a9690] hover:text-red-400 hover:border-red-400/50" title="Disable" data-testid={`gc-disable-${c.code}`}><Ban size={13}/></button>
                      <button onClick={() => { if (window.confirm(`Mark ${c.code} as refunded? This zeroes the balance.`)) action(c, "refund"); }} className="p-1.5 border border-white/15 text-[#9a9690] hover:text-red-400 hover:border-red-400/50" title="Refund" data-testid={`gc-refund-${c.code}`}><RotateCcw size={13}/></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {detail && <GiftCardDrawer card={detail} onClose={() => setDetail(null)}/>}

      {/* Issue comp dialog */}
      {showIssue && <IssueDialog onClose={() => setShowIssue(false)} onIssued={() => { setShowIssue(false); refresh(); }}/>}
    </div>
  );
}

function StatTile({ label, value, accent, testid }) {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-4" data-testid={testid}>
      <div className="font-ui text-[9px] tracking-[0.32em] uppercase text-[#C9A055]">{label}</div>
      <div className={`font-display-saira text-2xl mt-1 ${accent ? "text-[#C9A055]" : "text-[#F5F2EB]"}`}>{value}</div>
    </div>
  );
}

function GiftCardDrawer({ card, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80" onClick={onClose}>
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-[#0a0a0a] border-l border-white/10 overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="gc-drawer">
        <div className="sticky top-0 bg-[#0a0a0a] border-b border-white/10 px-5 py-4 flex justify-between items-center">
          <div>
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055]">Gift Card Details</div>
            <div className="font-mono text-lg text-[#F5F2EB] mt-1">{card.code}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:text-[#C9A055]"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-5">
          <img src={`${API_BASE}/api/gift-cards/image/${card.code}.png`} alt="" className="w-full border border-[#C9A055]/30"/>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <Field l="Amount" v={`$${card.amount.toFixed(2)}`}/>
            <Field l="Balance" v={`$${card.balance.toFixed(2)}`} accent/>
            <Field l="Status" v={card.status}/>
            <Field l="Payment" v={`${card.payment_method} (${card.payment_status})`}/>
            <Field l="Purchaser" v={`${card.purchaser_name} · ${card.purchaser_email}`}/>
            <Field l="Recipient" v={`${card.recipient_name} · ${card.recipient_email}`}/>
            <Field l="Created" v={(card.created_at || "").slice(0,19).replace("T"," ")}/>
            <Field l="Expires" v={(card.expires_at || "").slice(0,10)}/>
          </dl>
          {card.personal_message && (
            <div>
              <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055]">Message</div>
              <div className="font-script text-xl text-[#D4B07B] italic mt-1">&ldquo;{card.personal_message}&rdquo;</div>
            </div>
          )}
          <div>
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-2">Audit Log</div>
            <div className="space-y-2 text-[12px]">
              {(card.audit_log || []).slice().reverse().map((a, i) => (
                <div key={i} className="border border-white/10 px-3 py-2">
                  <div className="flex justify-between text-[#9a9690]">
                    <span>{a.action}</span>
                    <span>{(a.at || "").slice(0,19).replace("T"," ")}</span>
                  </div>
                  <div className="text-[#E8E0CC] mt-1">{a.note}</div>
                  <div className="text-[10px] text-[#5a5650] mt-1">by {a.by}</div>
                </div>
              ))}
            </div>
          </div>
          {(card.redemptions || []).length > 0 && (
            <div>
              <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-2">Redemptions</div>
              <div className="space-y-2 text-[12px]">
                {card.redemptions.map((r, i) => (
                  <div key={i} className="border border-white/10 px-3 py-2 flex justify-between">
                    <span className="text-[#E8E0CC]">Order {r.order_number || r.order_id?.slice(0,8)}</span>
                    <span className="text-[#C9A055]">-${r.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ l, v, accent }) {
  return (
    <div>
      <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#737373]">{l}</div>
      <div className={accent ? "text-[#C9A055] font-semibold" : "text-[#F5F2EB]"}>{v}</div>
    </div>
  );
}

function IssueDialog({ onClose, onIssued }) {
  const [form, setForm] = useState({ amount: 50, recipient_name: "", recipient_email: "", personal_message: "", expires_in_months: 24, reason: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/admin/gift-cards/issue", form);
      toast.success(`Issued ${data.code} · $${data.amount.toFixed(2)}`);
      onIssued();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not issue"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-[#0a0a0a] border border-[#C9A055]/40 w-full max-w-md p-6 space-y-3" data-testid="gc-issue-dialog">
        <div className="flex justify-between items-center">
          <div>
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055]">Issue Comp Card</div>
            <h3 className="font-display-saira text-2xl text-[#F5F2EB]"><Gift size={20} className="inline -mt-0.5"/> Complimentary</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-[#9a9690] hover:text-[#F5F2EB]"><X size={18}/></button>
        </div>
        <label className="block"><span className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Amount ($)</span>
          <input data-testid="gc-issue-amount" type="number" min="1" step="0.01" required className="stc-input mt-1" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: parseFloat(e.target.value) }))}/>
        </label>
        <label className="block"><span className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Recipient name</span>
          <input data-testid="gc-issue-name" required className="stc-input mt-1" value={form.recipient_name} onChange={e => setForm(f => ({ ...f, recipient_name: e.target.value }))}/>
        </label>
        <label className="block"><span className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Recipient email</span>
          <input data-testid="gc-issue-email" type="email" required className="stc-input mt-1" value={form.recipient_email} onChange={e => setForm(f => ({ ...f, recipient_email: e.target.value }))}/>
        </label>
        <label className="block"><span className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Personal message</span>
          <textarea data-testid="gc-issue-message" rows={2} className="stc-input mt-1 font-body" maxLength={220} value={form.personal_message} onChange={e => setForm(f => ({ ...f, personal_message: e.target.value }))}/>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Expires in (months)</span>
            <input type="number" min="1" max="120" className="stc-input mt-1" value={form.expires_in_months} onChange={e => setForm(f => ({ ...f, expires_in_months: parseInt(e.target.value) || 24 }))}/>
          </label>
          <label className="block"><span className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Audit reason</span>
            <input data-testid="gc-issue-reason" className="stc-input mt-1" placeholder="Giveaway · make-good · etc." value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}/>
          </label>
        </div>
        <button data-testid="gc-issue-submit" disabled={busy} type="submit" className="btn-gold w-full">{busy ? "Issuing…" : "Issue gift card"}</button>
      </form>
    </div>
  );
}
